// เนื้อเรื่องหนอน: ไส้เดือน ปลิง หนอนทะเล หนอนตัวกลม และหนอนตัวแบน

import type { GameEvent } from "../../engine/types";

const W = ["WORM"] as const;
const ANNELID = ["worm.annelid"];
const LEECHES = ["Hirudinidae", "Haemadipsidae", "Glossiphoniidae", "Erpobdellidae", "Haemopidae"];
const POLYCHAETES = ["Nereididae", "Eunicidae", "Serpulidae", "Sabellidae", "Arenicolidae", "Onuphidae", "Polynoidae", "Terebellidae", "Glyceridae", "Amphinomidae"];
// ไส้เดือนเป็นค่าเริ่มต้นของกลุ่มย่อย ปลิงและหนอนทะเลมีเรื่องของตัวเอง
const EARTHWORM = { subgroups: ANNELID, notFamilies: [...LEECHES, ...POLYCHAETES] };

export const WORM_EVENTS: readonly GameEvent[] = [
  // ---------- ไส้เดือน ----------
  {
    id: "earthworm_soil",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: EARTHWORM,
    weight: 8,
    text: "คุณกินดินผสมเศษใบไม้ผุ แล้วถ่ายออกมาเป็นมูลดินร่วนซุย",
    fact: "ไส้เดือนช่วยพรวนดินและเปลี่ยนเศษใบไม้เป็นปุ๋ย ชาลส์ ดาร์วินเคยเขียนหนังสือเกี่ยวกับไส้เดือนทั้งเล่ม",
    choices: [
      { label: "ชอนไชลงไปในดินลึก", outcomes: [{ chance: 1, text: "ดินชั้นล่างเย็นชื้นและปลอดภัย", effects: { fullness: 25, safety: 10 } }] },
      {
        label: "ขึ้นมากินเศษใบไม้บนผิวดินตอนกลางคืน",
        outcomes: [
          { chance: 0.75, text: "ใบไม้ผุนิ่มกินง่าย คุณลากมันลงรูไปหลายใบ", effects: { fullness: 35 } },
          { chance: 0.25, text: "คางคกที่ซุ่มรออยู่งับโดนท้ายตัวคุณ", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "earthworm_rain",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: EARTHWORM,
    weight: 5,
    text: "ฝนตกหนักจนน้ำท่วมรูของคุณ",
    fact: "ไส้เดือนหายใจผ่านผิวหนัง ผิวจึงต้องชื้นอยู่เสมอ ถ้าตัวแห้งจะหายใจไม่ได้",
    choices: [
      {
        label: "ขึ้นมาบนผิวดิน",
        outcomes: [
          { chance: 0.6, text: "ดินเปียกฉ่ำ คุณคลานไปหาที่อยู่ใหม่ได้สบาย", effects: { fullness: 10 } },
          { chance: 0.4, text: "ฝนหยุดแล้วแดดออก คุณติดอยู่บนลานปูนร้อนๆ", effects: { health: -25, cause: "disaster" } },
        ],
      },
      {
        label: "อยู่ในรูต่อไป",
        outcomes: [
          { chance: 0.7, text: "น้ำค่อยๆ ซึมลงดินไป คุณรอดมาได้", effects: { health: -5 } },
          { chance: 0.3, text: "น้ำขังอยู่นานจนคุณอ่อนแรง", effects: { health: -15, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "earthworm_bird",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: EARTHWORM,
    weight: 5,
    text: "นกกางเขนบ้านเดินเอียงคอฟังเสียงอยู่บนสนามหญ้าเหนือตัวคุณ",
    choices: [
      {
        label: "หยุดนิ่ง",
        outcomes: [
          { chance: 0.7, text: "นกเดินผ่านไปจิกไส้เดือนตัวอื่น", effects: { safety: 5 } },
          { chance: 0.3, text: "นกจิกดึงคุณขึ้นมาครึ่งตัว คุณดิ้นหลุดกลับลงรูได้", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "มุดลงลึกทันที",
        outcomes: [
          { chance: 0.85, text: "คุณมุดหนีลงไปพ้นปากนก", effects: { fullness: -5 } },
          { chance: 0.15, text: "นกได้ยินเสียงคุณมุดดิน และจิกลงมาตรงจุดนั้นพอดี", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "earthworm_spade",
    archetypes: W,
    stages: ["adult", "elder"],
    requires: EARTHWORM,
    weight: 2.5,
    text: "ใบจอบของชาวสวนสับลงมาในดินที่คุณอยู่",
    fact: "ไส้เดือนที่ถูกตัดขาดไม่ได้กลายเป็นสองตัว ส่วนใหญ่มีเพียงส่วนหัวที่อาจรอดและงอกหางใหม่",
    choices: [
      {
        label: "รีบมุดหนี",
        outcomes: [
          { chance: 0.6, text: "ใบจอบสับพลาดตัวคุณไปนิดเดียว", effects: { safety: -5 } },
          { chance: 0.3, text: "ใบจอบตัดหางคุณขาด ส่วนหัวยังรอดและจะงอกหางใหม่", effects: { health: -35, cause: "human" } },
          { chance: 0.1, text: "ใบจอบสับลงกลางตัวคุณพอดี", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "earthworm_mate",
    archetypes: W,
    stages: ["adult"],
    requires: { ...EARTHWORM, notFlags: ["has_mate"] },
    weight: 14,
    text: "คืนที่ดินชื้น คุณโผล่ขึ้นมาเจอไส้เดือนอีกตัวที่ปากรูข้างๆ",
    fact: "ไส้เดือนมีทั้งสองเพศในตัวเดียว หลังแลกเปลี่ยนสเปิร์มกัน ปลอกนูนรอบตัวจะสร้างปลอกห่อไข่ไว้",
    choices: [
      {
        label: "เลื้อยไปแนบตัวกัน",
        outcomes: [
          { chance: 0.85, text: "พวกคุณแลกเปลี่ยนสเปิร์มกันจนเกือบรุ่งเช้า", effects: { addFlags: ["has_mate"] } },
          { chance: 0.15, text: "แสงไฟฉายส่องมา อีกตัวหดกลับลงรูไป" },
        ],
      },
      { label: "หดกลับลงรู", outcomes: [{ chance: 1, text: "คุณกลับไปกินดินต่อ", effects: { fullness: 10 } }] },
    ],
  },
  {
    id: "earthworm_cocoon",
    archetypes: W,
    stages: ["adult"],
    requires: { ...EARTHWORM, flags: ["has_mate"] },
    weight: 30,
    text: "ปลอกนูนรอบตัวคุณเริ่มสร้างเมือกหนาเป็นวง",
    choices: [
      { label: "ถอดปลอกไข่ไว้ในดินชื้น", outcomes: [{ chance: 1, text: "คุณถอยตัวออกจากวงเมือก มันปิดหัวท้ายกลายเป็นปลอกไข่ที่มีไข่ {n} ฟอง", effects: { offspring: "clutch", fullness: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } }] },
    ],
  },

  // ---------- ปลิง ----------
  {
    id: "leech_blood",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ANNELID, families: LEECHES },
    weight: 9,
    text: "คุณรู้สึกถึงไออุ่นและแรงสั่นสะเทือน มีสัตว์ตัวใหญ่กำลังเดินผ่านมา",
    fact: "ปลิงและทากปล่อยสารที่ทำให้เลือดไม่แข็งตัวออกมาพร้อมน้ำลาย บางชนิดมีสารชาด้วย เหยื่อจึงมักไม่รู้ตัวว่าถูกดูด",
    choices: [
      {
        label: "ยืดตัวไปเกาะ",
        outcomes: [
          { chance: 0.7, text: "คุณดูดเลือดจนตัวพองหลายเท่า แล้วหลุดออกมาเอง อิ่มไปอีกหลายเดือน", effects: { fullness: 50 } },
          { chance: 0.3, text: "เขาเห็นคุณที่ข้อเท้า แล้วโรยเกลือใส่", effects: { health: -30, cause: "human" } },
        ],
      },
      { label: "รอเหยื่อตัวต่อไป", outcomes: [{ chance: 1, text: "คุณเกาะนิ่งอยู่บนใบไม้ชื้น เฝ้ารอต่อไป", effects: { fullness: -5, safety: 5 } }] },
    ],
  },
  {
    id: "leech_pond",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ANNELID, families: LEECHES, habitat: ["freshwater", "wetland"] },
    weight: 5,
    text: "ปลาซิวฝูงหนึ่งว่ายวนอยู่เหนือตัวคุณในบ่อน้ำ",
    choices: [
      {
        label: "ว่ายเป็นลอนคลื่นขึ้นไปเกาะ",
        outcomes: [
          { chance: 0.5, text: "คุณเกาะปลาตัวหนึ่งได้และดูดเลือดจนอิ่ม", effects: { fullness: 40 } },
          { chance: 0.5, text: "ปลาตัวใหญ่ที่ตามมาข้างหลังงับคุณแทน", effects: { health: -25, cause: "predator" } },
        ],
      },
      { label: "เกาะใต้ใบบัวต่อไป", outcomes: [{ chance: 1, text: "คุณกินหอยตัวเล็กที่ผ่านมาแทน", effects: { fullness: 15 } }] },
    ],
  },

  // ---------- หนอนทะเล ----------
  {
    id: "polychaete_tube",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ANNELID, families: POLYCHAETES },
    weight: 8,
    text: "คุณคืบคลานไปตามพื้นทรายใต้ทะเลด้วยขาเล็กๆ ที่มีขนแข็งเป็นพุ่ม",
    fact: "หนอนทะเลหลายชนิดสร้างท่อจากเมือกและเม็ดทรายไว้อยู่ แล้วยื่นหนวดคล้ายขนนกออกมาจับอาหาร",
    choices: [
      { label: "ยื่นหนวดจากท่อจับอาหาร", outcomes: [{ chance: 1, text: "หนวดคล้ายขนนกกวาดแพลงก์ตอนเข้าปาก แล้วหดกลับทันทีที่มีเงาผ่าน", effects: { fullness: 25, safety: 10 } }] },
      {
        label: "ออกล่าหนอนตัวอื่น",
        outcomes: [
          { chance: 0.7, text: "ขากรรไกรคมของคุณจับหนอนตัวเล็กได้", effects: { fullness: 35 } },
          { chance: 0.3, text: "ปลาที่หากินตามพื้นทรายเห็นคุณ", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "polychaete_swarm",
    archetypes: W,
    stages: ["adult"],
    requires: { subgroups: ANNELID, families: POLYCHAETES, notFlags: ["has_mate"] },
    weight: 12,
    text: "คืนนี้พระจันทร์สว่าง หนอนทะเลนับพันพากันว่ายขึ้นสู่ผิวน้ำ",
    fact: "หนอนทะเลบางชนิดขึ้นมาว่ายผสมพันธุ์พร้อมกันเป็นฝูงใหญ่ตามจังหวะข้างขึ้นข้างแรม",
    choices: [
      {
        label: "ว่ายขึ้นไปร่วมฝูง",
        outcomes: [
          { chance: 0.75, text: "ผิวน้ำเต็มไปด้วยไข่และสเปิร์ม คุณได้ผสมพันธุ์", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.25, text: "ปลาฝูงใหญ่มารอกินหนอนอยู่ที่ผิวน้ำ", effects: { health: -30, cause: "predator" } },
        ],
      },
      { label: "อยู่ในท่อ", outcomes: [{ chance: 1, text: "คุณรอคืนอื่น" }] },
    ],
  },

  // ---------- หนอนตัวกลม ----------
  {
    id: "nematode_film",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["worm.nematode"] },
    weight: 8,
    text: "คุณว่ายบิดตัวไปมาในฟิล์มน้ำบางๆ ระหว่างเม็ดดิน",
    fact: "หนอนตัวกลมพบได้แทบทุกที่บนโลก ดินเพียงหนึ่งกำมืออาจมีหนอนตัวกลมนับพันตัว",
    choices: [
      { label: "กินแบคทีเรียรอบรากพืช", outcomes: [{ chance: 1, text: "รอบรากพืชมีแบคทีเรียให้กินไม่หมด", effects: { fullness: 30 } }] },
      {
        label: "ว่ายตามฟิล์มน้ำไปหาที่ใหม่",
        outcomes: [
          { chance: 0.7, text: "คุณเจอผลไม้เน่าที่มีแบคทีเรียเต็มไปหมด", effects: { fullness: 35 } },
          { chance: 0.3, text: "ไรตัวห้ำในดินจับคุณได้", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "nematode_dauer",
    archetypes: W,
    stages: ["juvenile"],
    requires: { subgroups: ["worm.nematode"] },
    weight: 5,
    text: "อาหารรอบตัวหมดลง และดินก็เริ่มแห้ง",
    fact: "หนอนตัวกลมบางชนิดหยุดการเจริญเติบโตเข้าสู่ระยะพักตัวเมื่ออาหารขาดแคลน และรอได้นานหลายเดือน",
    choices: [
      { label: "หยุดโตแล้วเข้าสู่ระยะพักตัว", outcomes: [{ chance: 1, text: "ร่างกายของคุณปิดตัวเองแน่น รอจนฝนกลับมาพร้อมอาหาร", effects: { fullness: 15, health: 5, safety: 10 } }] },
      {
        label: "ฝืนหาอาหารต่อ",
        outcomes: [
          { chance: 0.4, text: "คุณเจอแบคทีเรียกลุ่มสุดท้ายพอดี", effects: { fullness: 20 } },
          { chance: 0.6, text: "ดินแห้งเร็วกว่าที่คิด ร่างกายของคุณเหี่ยวลง", effects: { health: -20, cause: "starvation" } },
        ],
      },
    ],
  },
  {
    id: "nematode_fungus",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["worm.nematode"] },
    weight: 4,
    text: "ข้างหน้ามีเส้นใยราขดเป็นห่วงเล็กๆ เรียงกันอยู่",
    fact: "เชื้อราบางชนิดสร้างบ่วงจิ๋วไว้รัดจับหนอนตัวกลม แล้วย่อยกินเป็นอาหาร",
    choices: [
      {
        label: "ว่ายอ้อมไปทางอื่น",
        outcomes: [
          { chance: 0.8, text: "คุณเลี่ยงบ่วงได้ทั้งหมด", effects: { fullness: -5 } },
          { chance: 0.2, text: "อ้อมไปก็ยังเจอบ่วงอีกชุด ห่วงรัดตัวคุณแน่นในพริบตา", effects: { death: "predator" } },
        ],
      },
      {
        label: "ลอดผ่านไปเร็วๆ",
        outcomes: [
          { chance: 0.5, text: "คุณลอดผ่านไปได้ อีกฝั่งมีอาหารเยอะ", effects: { fullness: 25 } },
          { chance: 0.5, text: "ห่วงพองตัวรัดคุณแน่น เส้นใยราค่อยๆ ชอนไชเข้ามา", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "nematode_insect",
    archetypes: W,
    stages: ["juvenile"],
    requires: { subgroups: ["worm.nematode"], families: ["Steinernematidae", "Heterorhabditidae"] },
    weight: 8,
    text: "ตัวอ่อนด้วงตัวอ้วนกำลังกัดกินรากพืชอยู่ใกล้ๆ",
    fact: "หนอนตัวกลมบางชนิดมุดเข้าไปในตัวแมลง แล้วปล่อยแบคทีเรียที่ฆ่าแมลงจากข้างใน เกษตรกรจึงใช้มันกำจัดแมลงศัตรูพืช",
    choices: [
      { label: "มุดเข้าไปในตัวมัน", outcomes: [{ chance: 1, text: "แบคทีเรียที่คุณพามาจัดการเหยื่อในไม่กี่วัน คุณกินและเติบโตอยู่ข้างใน", effects: { fullness: 45 } }] },
      { label: "รอเหยื่อที่ตัวเล็กกว่า", outcomes: [{ chance: 1, text: "คุณรอนานแต่ก็ปลอดภัย", effects: { fullness: -5, safety: 5 } }] },
    ],
  },

  // ---------- หนอนตัวแบน ----------
  {
    id: "flatworm_glide",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["worm.flatworm"] },
    weight: 8,
    text: "คุณไถลไปบนเมือกที่ตัวเองสร้าง โดยใช้ขนเล็กๆ ใต้ท้องโบกพา",
    fact: "พลานาเรียมีจุดตาที่รับรู้แสงได้ และมักหนีแสงไปอยู่ใต้ก้อนหิน",
    choices: [
      { label: "ตามกลิ่นซากไปหาอาหาร", outcomes: [{ chance: 1, text: "คุณเจอซากหนอนตัวเล็ก แล้วยื่นท่อกินอาหารจากกลางท้องออกไปดูด", effects: { fullness: 30 } }] },
      { label: "หลบใต้ก้อนหินหนีแสง", outcomes: [{ chance: 1, text: "ใต้ก้อนหินมืดและเย็น ปลาหาคุณไม่เจอ", effects: { safety: 10, fullness: 5 } }] },
    ],
  },
  {
    id: "flatworm_regen",
    archetypes: W,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["worm.flatworm"] },
    weight: 4,
    text: "ปลาตัวเล็กงับโดนครึ่งหลังของลำตัวคุณ",
    fact: "พลานาเรียที่ถูกตัดเป็นชิ้นเล็กๆ งอกกลับเป็นตัวใหม่ได้ครบทุกชิ้น",
    choices: [
      { label: "ปล่อยส่วนนั้นไปแล้วหนี", outcomes: [{ chance: 1, text: "คุณเสียลำตัวไปครึ่งหนึ่ง แต่ไม่กี่สัปดาห์ต่อมาก็งอกกลับมาครบ", effects: { health: -10, safety: 5 } }] },
      {
        label: "ดิ้นสู้",
        outcomes: [
          { chance: 0.5, text: "คุณดิ้นหลุดมาได้ทั้งตัว", effects: { health: -15 } },
          { chance: 0.5, text: "ปลากลืนคุณลงไปทั้งตัว", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "flatworm_fission",
    archetypes: W,
    stages: ["adult"],
    requires: { subgroups: ["worm.flatworm"] },
    weight: 4,
    text: "คุณยึดส่วนหัวไว้กับก้อนหิน แล้วค่อยๆ ดึงส่วนหางไปอีกทาง",
    fact: "พลานาเรียหลายชนิดสืบพันธุ์ได้ด้วยการฉีกตัวเองออกเป็นสองท่อน",
    choices: [
      { label: "ดึงต่อไปจนขาด", outcomes: [{ chance: 1, text: "ลำตัวคุณขาดเป็นสองท่อน ท่อนหางจะงอกเป็นตัวใหม่ คุณมีลูกหลานเพิ่มอีก {n} ตัว", effects: { offspringCount: 1, fullness: -15 } }] },
      { label: "ยังไม่ใช่ตอนนี้", outcomes: [{ chance: 1, text: "คุณปล่อยหินแล้วไถลไปหาอาหารก่อน", effects: { fullness: 10 } }] },
    ],
  },
  {
    id: "flatworm_fencing",
    archetypes: W,
    stages: ["adult"],
    requires: { subgroups: ["worm.flatworm"], notFlags: ["has_mate"] },
    weight: 12,
    text: "คุณเจอหนอนตัวแบนอีกตัว ทั้งคู่ต่างยกส่วนหน้าขึ้นเผชิญหน้ากัน",
    fact: "หนอนตัวแบนทะเลบางชนิดมีทั้งสองเพศ ตอนผสมพันธุ์จะต่อสู้กันด้วยอวัยวะเพศ ฝ่ายที่แพ้ต้องรับบทเป็นแม่",
    choices: [
      {
        label: "ดวลกัน",
        outcomes: [
          { chance: 0.5, text: "คุณชนะ อีกตัวต้องเป็นฝ่ายออกไข่", effects: { addFlags: ["has_mate"] } },
          { chance: 0.5, text: "คุณแพ้ และต้องเป็นฝ่ายออกไข่เอง", effects: { addFlags: ["has_mate"], health: -5 } },
        ],
      },
      { label: "ไถลหนีไป", outcomes: [{ chance: 1, text: "คุณไม่อยากดวลกับใคร" }] },
    ],
  },
  {
    id: "liver_fluke_fish",
    archetypes: W,
    stages: ["juvenile"],
    requires: { subgroups: ["worm.flatworm"], families: ["Opisthorchiidae"] },
    once: true,
    weight: 10,
    text: "คุณเป็นตัวอ่อนซ่อนอยู่ในเนื้อปลาตะเพียน แล้ววันหนึ่งปลาตัวนั้นก็ถูกจับขึ้นมา",
    fact: "พยาธิใบไม้ตับแพร่สู่คนที่กินปลาน้ำจืดดิบหรือสุกๆ ดิบๆ และเป็นสาเหตุสำคัญของมะเร็งท่อน้ำดีในภาคอีสาน",
    choices: [
      {
        label: "รอดูชะตากรรม",
        outcomes: [
          { chance: 0.5, text: "ปลาถูกนำไปทำก้อยปลาดิบ คุณได้เข้าไปอยู่ในท่อน้ำดีของคนที่กิน", effects: { fullness: 40, safety: 10 } },
          { chance: 0.5, text: "ปลาถูกนำไปต้มจนสุก", effects: { death: "human" } },
        ],
      },
    ],
  },
];
